import { useLayoutEffect, useRef, useState } from 'react';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';
import { Summary as IssueSummary } from '@/types';

// Collapse the chips to this many rows; anything beyond is revealed via "Show more".
const VISIBLE_ROWS = 1;

export function MatterIssues({
  issues,
  onSelectIssue,
  matterId,
  context = 'Matter',
}: {
  issues: IssueSummary[];
  onSelectIssue: (issue: IssueSummary) => void;
  matterId: string;
  context?: 'Matter' | 'Subset';
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);

  // Group the chips into rows by their vertical position to find out whether they
  // span more than VISIBLE_ROWS, and how tall the clamped container should be.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const children = Array.from(el.children) as HTMLElement[];
      if (children.length === 0) {
        setIsOverflowing(false);
        setCollapsedHeight(null);
        return;
      }

      const containerTop = el.getBoundingClientRect().top;
      const rowTops: number[] = [];
      for (const child of children) {
        const top = Math.round(child.getBoundingClientRect().top - containerTop);
        if (!rowTops.some((t) => Math.abs(t - top) < 1)) rowTops.push(top);
      }
      rowTops.sort((a, b) => a - b);

      const overflow = rowTops.length > VISIBLE_ROWS;
      setIsOverflowing(overflow);

      if (overflow) {
        const lastVisibleTop = rowTops[VISIBLE_ROWS - 1];
        // Bottom of the tallest chip in the last visible row, relative to the container top.
        const lastRowBottom = Math.max(
          ...children
            .filter((c) => Math.abs(Math.round(c.getBoundingClientRect().top - containerTop) - lastVisibleTop) < 1)
            .map((c) => c.getBoundingClientRect().bottom - containerTop),
        );
        setCollapsedHeight(lastRowBottom);
      } else {
        setCollapsedHeight(null);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [issues]);

  const isClamped = !isExpanded && isOverflowing && collapsedHeight != null;

  return (
    <div className="my-4.5">
      <h2 className="mb-1 font-medium">{context} issues</h2>
      <p className="mb-4 text-[13px] text-gray-500">
        Select an issue to see a comprehensive overview of its significance in this {context}.
      </p>

      <div
        ref={containerRef}
        className="-mx-1 mt-3 flex flex-wrap gap-1.5 gap-y-1.75 overflow-hidden"
        style={isClamped ? { maxHeight: collapsedHeight ?? undefined } : undefined}
      >
        {issues.map((issue) => (
          <Button
            key={issue.name}
            data-issue-chip
            onClick={() => onSelectIssue(issue)}
            className="gap-1 rounded-xl border-blue-200 bg-blue-50/50 px-3 py-1 pr-2 text-[13px] font-normal text-blue-500 hover:border-blue-400/80 hover:bg-blue-50/60 hover:text-blue-600"
          >
            {issue.name} <Icon className="lucide--arrow-right text-blue-500!" />
          </Button>
        ))}
      </div>

      {isOverflowing && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-3.5 px-0 text-blue-500 hover:text-blue-600"
          onClick={() =>
            setIsExpanded((prev) => {
              if (!prev) trackCustomEvent(TrackingEventsV2.MatterExpandIssuesFromOverview, { matterId, context });
              return !prev;
            })
          }
        >
          {isExpanded ? 'Show less' : 'Show more'}
          <Icon className={cn(isExpanded ? 'rotate-180' : '', 'lucide--chevron-down')} />
        </Button>
      )}
    </div>
  );
}
